import React, { useEffect, useState } from 'react';
import DefaultModal from '../../../../components/default/DefaultModal';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../../../components/buttons/ButtonCancelForm';
import useTransportService from '../../../../app/services/hooks/useTransportService';
import { Delivery, DeliveryFilters } from '../../../../app/models/Delivery';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { momentParseDate } from '../../../../helpers';

interface Props {
    show: boolean;
    handleClose: () => void;
    transportId?: number;
    selectedDeliveryIds: number[];
    onSelect: (deliveryIds: number[]) => void;
}

const DeliverySelectionModal = ({
    show,
    handleClose,
    transportId,
    selectedDeliveryIds,
    onSelect
}: Props) => {
    const { getAvailableDeliveries, fetchingGetAvailableDeliveries } = useTransportService();
    
    const [deliveries, setDeliveries] = useState<Delivery[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedIds, setSelectedIds] = useState<number[]>(selectedDeliveryIds);
    const [filter, setFilter] = useState<DeliveryFilters & { transport_id?: number }>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        transport_id: transportId
    });

    useEffect(() => {
        if (show) {
            setSelectedIds(selectedDeliveryIds);
            // Resetear filtro al abrir el modal
            setFilter({
                page: 1,
                per_page: 10,
                sort: 'id',
                order: 'desc',
                transport_id: transportId
            });
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            loadDeliveries();
        }
    }, [show, filter]);

    const loadDeliveries = () => {
        getAvailableDeliveries(filter, {
            onSuccess: (response: ServiceResponse) => {
                setDeliveries(response.data.deliveries || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar entregas:', response.message);
                setDeliveries([]);
                setTotalRows(0);
            }
        });
    };

    const toggleDeliverySelection = (deliveryId: number) => {
        setSelectedIds(prev => {
            if (prev.includes(deliveryId)) {
                return prev.filter(id => id !== deliveryId);
            } else {
                return [...prev, deliveryId];
            }
        });
    };

    const handleSelectAll = () => {
        const allIds = deliveries.map(d => d.id!).filter(id => id !== undefined);
        if (selectedIds.length === allIds.length && allIds.every(id => selectedIds.includes(id))) {
            // Deseleccionar todas
            setSelectedIds(selectedDeliveryIds);
        } else {
            // Seleccionar todas (mantener las ya seleccionadas)
            const newIds = [...new Set([...selectedIds, ...allIds])];
            setSelectedIds(newIds);
        }
    };

    const handleAccept = () => {
        onSelect(selectedIds);
    };

    const columns = [
        {
            name: '',
            selector: (row: any) => row.id,
            sortable: false,
            width: '50px',
            cell: (row: Delivery) => (
                <input
                    type="checkbox"
                    checked={selectedIds.includes(row.id!)}
                    onChange={() => toggleDeliverySelection(row.id!)}
                />
            )
        },
        {
            name: 'ID',
            selector: (row: any) => row.id || 0,
            sortable: true,
            sortField: 'id',
            width: '80px',
            cell: (row: Delivery) => (
                <span>{row.id || '-'}</span>
            )
        },
        {
            name: 'Pedido',
            selector: (row: any) => row.sales_order || `Pedido #${row.sales_order_id}`,
            sortable: false,
            cell: (row: Delivery) => (
                <span>{row.sales_order || `Pedido #${row.sales_order_id}`}</span>
            )
        },
        {
            name: 'Cliente',
            selector: (row: any) => row.client || '',
            sortable: false,
            cell: (row: Delivery) => (
                <span dangerouslySetInnerHTML={{ __html: row.client || '' }} />
            )
        },
        {
            name: 'Destino',
            selector: (row: any) => row.destination || '',
            sortable: false,
            cell: (row: Delivery) => (
                <span>{row.destination || '-'}</span>
            )
        },
        {
            name: 'Estado',
            selector: (row: any) => row.status_display || row.status || '',
            sortable: true,
            sortField: 'status',
            cell: (row: Delivery) => (
                <span dangerouslySetInnerHTML={{ __html: row.status_display || row.status || '-' }} />
            )
        },
        {
            name: 'Fecha Entrega',
            selector: (row: any) => row.delivery_date || '',
            sortable: true,
            sortField: 'delivery_date',
            width: '120px',
            cell: (row: Delivery) => (
                <span>
                    {row.delivery_date 
                        ? momentParseDate(row.delivery_date)
                        : '-'
                    }
                </span>
            )
        }
    ];

    return (
        <>
            {/* Overlay oscuro para diferenciar del modal padre */}
            {show && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1054,
                    }}
                />
            )}
            <DefaultModal
                show={show}
                handleClose={handleClose}
                title="Buscar y Seleccionar Entregas"
                size="lg"
                backdrop={true}
                showFooter={false}
                dialogStyle={{ zIndex: 1055 }}
            >
            <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="Buscar por ID de entrega..."
                                onChange={(e) => {
                                    const searchTerm = e.target.value;
                                    setFilter({
                                        ...filter,
                                        id: searchTerm ? searchTerm : undefined,
                                        page: 1
                                    });
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        loadDeliveries();
                                    }
                                }}
                            />
                    </div>
                    <div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleSelectAll}
                        >
                            {selectedIds.length === deliveries.length && deliveries.length > 0
                                ? 'Deseleccionar Todas'
                                : 'Seleccionar Todas'
                            }
                        </button>
                    </div>
                </div>
                <small className="text-muted">
                    {selectedIds.length} entrega(s) seleccionada(s)
                </small>
            </div>

            <DefaultDataTable
                columns={columns}
                data={deliveries}
                progressPending={fetchingGetAvailableDeliveries}
                paginationTotalRows={totalRows}
                filter={filter}
                setFilter={setFilter}
                selectableRows={false}
            />

            <div className="mt-3 d-flex justify-content-end gap-2">
                <ButtonCancelForm callbackCancel={handleClose} />
                <ButtonSaveForm
                    callbackSave={handleAccept}
                    title="Aceptar"
                    locked={false}
                />
            </div>
        </DefaultModal>
        </>
    );
};

export default DeliverySelectionModal;
